import React, { FC, useState } from 'react';
import { Box, Button } from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import SchedulingActions from './scheduling-actions.component';
import { ProductionPlanningStep } from 'models/production-planning';
import { ConstructionForTemplateMatching } from './scheduling-actions.component';
import { useProductionSchedulingLogic } from 'hooks/useProductionSchedulingLogic.hook';
import ProductionSchedulingTable from './production-scheduling-table/production-scheduling-table.component';
import ProductionSchedulingAlerts from './production-scheduling-alerts/production-scheduling-alerts.component';
import AutoSchedulingPanel from './auto-scheduling-panel/auto-scheduling-panel.component';
import PlanOverviewDialog from './plan-overview-dialog/plan-overview-dialog.component';
import { useTranslation } from 'react-i18next';

type ProductionSchedulingProps = {
    steps: ProductionPlanningStep[];
    onStepsChange: (steps: ProductionPlanningStep[]) => void;
    internalDeliveryDate?: Date | null;
    constructions?: ConstructionForTemplateMatching[];
    onValidChange?: (isValid: boolean) => void;
    sampleType?: string;
    disablePastDateWarnings?: boolean;
    orderId?: string;
    disableAutoTemplate?: boolean;
    clinicId?: string;
};

const ProductionScheduling: FC<ProductionSchedulingProps> = ({
    steps,
    onStepsChange,
    internalDeliveryDate,
    constructions,
    onValidChange,
    sampleType,
    disablePastDateWarnings,
    orderId,
    disableAutoTemplate,
    clinicId,
}) => {
    const { t: translate } = useTranslation(['orders']);
    const [showPlanOverview, setShowPlanOverview] = useState(false);
    const hasScheduledSteps = steps.some((s) => s.startDate && s.deadline);

    const {
        direction,
        setDirection,
        startDate,
        setStartDate,
        autoScheduling,
        runFullAutoSchedule,
        handleStepChange,
        handleDeleteStep,
        handleAddStep,
        handleDragEnd,
        handleMoveStepToNextDay,
        handleTimeInputValidChange,
        isTimeInputInvalid,
        isDeadlineInvalid,
        isStartDateInvalid,
        warnings,
        errors,
    } = useProductionSchedulingLogic({
        steps,
        onStepsChange,
        internalDeliveryDate,
        onValidChange,
        disablePastDateWarnings,
    });

    return (
        <>
            <Box mb={3}>
                <AutoSchedulingPanel
                    direction={direction}
                    onDirectionChange={setDirection}
                    startDate={startDate}
                    onStartDateChange={setStartDate}
                    internalDeliveryDate={internalDeliveryDate}
                    loading={autoScheduling}
                    onCalculate={runFullAutoSchedule}
                    canCalculate={steps.length > 0}
                />
            </Box>

            <ProductionSchedulingTable
                steps={steps}
                orderId={orderId}
                onDragEnd={handleDragEnd}
                onStepChange={handleStepChange}
                onDeleteStep={handleDeleteStep}
                onMoveStepToNextDay={handleMoveStepToNextDay}
                isDeadlineInvalid={isDeadlineInvalid}
                isStartDateInvalid={isStartDateInvalid}
                isTimeInputInvalid={isTimeInputInvalid}
                onTimeInputValidChange={handleTimeInputValidChange}
            />

            <Box mt={2}>
                <SchedulingActions
                    steps={steps}
                    onStepsChange={onStepsChange}
                    constructions={constructions}
                    sampleType={sampleType}
                    onAddStep={handleAddStep}
                    disableAutoTemplate={disableAutoTemplate}
                    clinicId={clinicId}
                />
            </Box>

            <ProductionSchedulingAlerts errors={errors} warnings={warnings} />

            {hasScheduledSteps && (
                <Box mt={2}>
                    <Button size="small" startIcon={<TimelineIcon />} onClick={() => setShowPlanOverview(true)}>
                        {translate('orders:SHOW_PLAN_OVERVIEW')}
                    </Button>
                </Box>
            )}

            <PlanOverviewDialog open={showPlanOverview} onClose={() => setShowPlanOverview(false)} steps={steps} />
        </>
    );
};

export default ProductionScheduling;
